import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";

const HelpCenter = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast({
        title: "Please fill in all fields",
        description: "All fields are required to submit your message.",
        variant: "destructive"
      });
      return;
    }

    // Simulate form submission
    toast({
      title: "Message sent!",
      description: "We'll get back to you within 24 hours.",
    });

    // Reset form
    setFormData({
      name: "",
      email: "",
      subject: "",
      message: ""
    });
  };

  const faqData = [
    {
      question: "How many credits do I get with each plan?",
      answer: "The Free plan includes 12 credits to get you started. Paid plans offer 250, 500, 1200, or up to 5000 credits per month depending on your selection. Credits reset monthly, and unused credits do not roll over."
    },
    {
      question: "What counts as one credit?",
      answer: "Each coloring page generation uses 6 credits. This includes the full AI sketch process, theme application, and the ability to download your high-resolution printable image."
    },
    {
      question: "Can I upgrade or downgrade my plan?",
      answer: "Yes! You can upgrade or downgrade your plan anytime from your Profile page. Changes take effect immediately, and any billing adjustments will be applied automatically."
    },
    {
      question: "What image formats are supported?",
      answer: "We currently support PNG and JPEG image uploads. All generated coloring pages are delivered in high-resolution PNG format, optimized for printing on standard 8.5x11 inch paper."
    },
    {
      question: "Can I use the coloring pages commercially?",
      answer: "Yes, with the Theme Master and Super Studio plans, you receive full commercial usage rights for all generated pages. Other plans, including Little Artist and Creative Explorer, are for personal use only."
    },
    {
      question: "How do I reset my password?",
      answer: "Click the 'Forgot Password' link on the login page and enter your registered email address. We’ll send you a secure link to reset your password."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely! All data is encrypted using industry-standard security measures. Your images remain private and are only accessible through your account. We never sell your data."
    },
    {
      question: "Can I request custom themes?",
      answer: "Yes! We're always adding new themes based on user feedback. Use the contact form on our website to suggest themes you’d love to see."
    },
    {
      question: "Can I save or revisit my past creations?",
      answer: "Yes, all your generated coloring pages are saved to your Dashboard. You can download them again at any time while your account is active."
    },
    {
      question: "What age group is SKECHA suitable for?",
      answer: "SKECHA is designed for kids aged 4-10, but it's also perfect for teens, parents, teachers, and anyone who loves to colour. It's creative fun for all ages!"
    }
  ];


  return (
    <Layout
      seoTitle="Help Center - SKECHA"
      seoDescription="Get help with SKECHA. Browse FAQs, learn about credits and pricing, troubleshoot issues, and contact our support team for assistance."
      seoKeywords="help center, FAQ, support, customer service, troubleshooting, contact support"
      seoUrl="https://skecha.com/help"
      breadcrumbs={[
        { name: "Home", url: "https://skecha.com" },
        { name: "Help Center", url: "https://skecha.com/help" },
      ]}
    >
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Help Center</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Find answers to common questions or get in touch with our support team.
            </p>
          </div>

          {/* FAQ Section */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
              <CardDescription>
                Quick answers to the most common questions about SKECHA.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent>
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Support</CardTitle>
              <CardDescription>
                Can't find what you're looking for? Send us a message and we'll help you out.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What can we help you with?"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Please describe your question or issue in detail..."
                    className="min-h-[120px]"
                    required
                  />
                </div>
                
                <Button type="submit" className="w-full md:w-auto">
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </Layout>
  );
};

export default HelpCenter;